import { $isListItemNode } from '@lexical/list';
import { $findMatchingParent } from '@lexical/utils';
import {
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  type LexicalNode,
} from 'lexical';

import type { TextBlock } from '@/teoCore/TeoApi';

import type { TeoMarker } from '../EditorMarkerManager.svelte';
import { $isTeoTextNode, TeoTextNode } from './TeoTextNode';

export interface OffsetInNode {
  node: LexicalNode;
  offset: number;
}

export interface OffsetTeoMarker {
  start: OffsetInNode;
  end: OffsetInNode;
  marker: TeoMarker;
}

/**
 * Checks if the start of the current selection in the editor is within a node of the specified type.
 *
 * @param nodeType - The name of the type to check the node for.
 * @returns `true` if the current selection starts within a node of the specified type, otherwise `false`.
 */
export function $currentSelectionStartsInNodeType(nodeType: string): boolean {
  const selection = $getSelection();
  if (selection === null) return false;
  const startEnd = selection.getStartEndPoints();
  if (startEnd === null) return false;
  const startNode = startEnd[0].getNode();
  if (startNode.getType() === nodeType) return true;
  const parentMatchingNode = $findMatchingParent(
    startNode,
    (ancestor) => ancestor.getType() === nodeType,
  );
  return parentMatchingNode !== null;
}

/**
 * Creates a string representation of the content of a LexicalNode.
 * Tries to preserve the structure of paragraphs and line breaks with good readability.
 */
export function $getPlainText(node: LexicalNode): string {
  let textString = '';

  $traverseNodes(
    node,
    (node: LexicalNode) => {
      if ($isTeoTextNode(node)) {
        textString += node.getTextContent();
      } else if ($isListItemNode(node)) {
        textString += '- ';
      } else if ($isLineBreakNode(node)) {
        textString += '\n';
      } else if (!$isElementNode(node)) {
        textString += node.getTextContent();
      }
    },
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        if ($isParagraphNode(node)) {
          textString += '\n\n';
        } else {
          textString += '\n';
        }
      }
    },
  );
  return textString.trim();
}

/**
 * Creates a string representation of the content of a LexicalNode.
 * Keeps the character offsets consistent.
 */
export function $getTeoTextContent(node: LexicalNode): string {
  let textString: string = '';
  $traverseNodes(
    node,
    (node: LexicalNode) => {
      if (!$isElementNode(node)) {
        textString += node.getTextContent();
      }
    },
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        textString += '\n\n';
      }
    },
  );
  // Trim trailing paragraph and linebreak blocks from the end of the string
  return textString.replace(/\s+$/g, '');
}

export function $getTextArray(node: LexicalNode): TextBlock[] {
  const textArray: TextBlock[] = [];
  $traverseNodes(
    node,
    (node: LexicalNode) => {
      let newTextBlock: TextBlock | undefined = undefined;
      if ($isTeoTextNode(node)) {
        const string = node.getTextContent();
        if (string.length > 0) {
          newTextBlock = {
            string: string,
            type: 'text',
          };
        }
      } else if ($isListItemNode(node)) {
        newTextBlock = {
          string: '',
          type: 'text',
          length: 0,
          interpret_as: '\n- ',
        };
      } else if ($isLineBreakNode(node)) {
        newTextBlock = {
          string: '',
          type: 'linebreak',
          length: node.getTextContentSize(),
        };
      } else if (!$isElementNode(node)) {
        newTextBlock = {
          string: node.getTextContent(),
          type: 'text',
        };
        if (!['tab'].includes(node.getType())) {
          console.warn('Handling unknown node type in $getTextArray:', node.getType());
        }
      } else {
        if (!['root', 'paragraph'].includes(node.getType())) {
          console.warn('Ignoring unknown node type in $getTextArray:', node.getType());
        }
      }
      if (newTextBlock) {
        textArray.push(newTextBlock);
      }
    },
    // postCallback:
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        textArray.push({
          string: '\n\n',
          type: 'paragraph',
          length: 2,
        });
      }
    },
  );
  // Trim trailing paragraph and linebreak blocks from the end of the array
  while (
    textArray.length > 0 &&
    (textArray[textArray.length - 1]?.type === 'paragraph' ||
      textArray[textArray.length - 1]?.type === 'linebreak')
  ) {
    textArray.pop();
  }
  return textArray;
}

/**
 * Traverses a LexicalNode tree in pre-order (node, then children), with optional post-order callback.
 *
 * @param node - The root LexicalNode to start traversal from.
 * @param callback - Function called on each node before its children.
 *                   If it returns false, traversal of that branch is stopped.
 * @param postCallback - Optional function called on each node after its children (post-order).
 *
 * @returns false if traversal was stopped early, otherwise undefined.
 */
export function $traverseNodes(
  node: LexicalNode,
  callback: (node: LexicalNode) => boolean | void,
  postCallback?: (node: LexicalNode) => void,
) {
  const shouldContinue = callback(node);
  if (shouldContinue === false) return false;
  if ($isElementNode(node)) {
    node.getChildren().forEach((child: LexicalNode) => {
      $traverseNodes(child, callback, postCallback);
    });
  }
  postCallback?.(node);
}

export function $getLeafNodes(node: LexicalNode): LexicalNode[] {
  if (!$isElementNode(node)) {
    return [node];
  }
  const nodes: LexicalNode[] = [];
  node.getChildren().forEach((child: LexicalNode) => {
    nodes.push(...$getLeafNodes(child));
  });
  return nodes;
}

/**
 * Transforms a `TeoMarker` (which uses absolute offsets) into an `OffsetTeoMarker`
 * (which uses node-relative offsets) by traversing the given Lexical tree.
 *
 * This function walks the tree rooted at `rootNode`, mapping the absolute start and end
 * offsets of the marker to specific text nodes and their corresponding offsets.
 *
 * @param marker - The `TeoMarker` object containing absolute start and end offsets.
 * @param rootNode - The root `LexicalNode` of the tree to traverse.
 * @returns An `OffsetTeoMarker` with node-relative offsets if mapping is successful,
 *          or `null` if the marker could not be mapped (e.g., out of bounds).
 */
export function $transformMarkerToOffsetMarker(
  marker: TeoMarker,
  rootNode: LexicalNode,
): OffsetTeoMarker | null {
  let start: OffsetInNode | null = null;
  let end: OffsetInNode | null = null;
  let lastTextNode: TeoTextNode | null = null;
  let currentOffset = 0;

  // Traversal callback
  function visit(node: LexicalNode): boolean | void {
    let nextOffset = currentOffset;
    if (!$isElementNode(node)) {
      nextOffset += node.getTextContentSize();
    }

    if ($isTeoTextNode(node)) {
      if (!start && marker.start < nextOffset) {
        const offsetInNode = marker.start - currentOffset;
        start = {
          node,
          offset: Math.max(offsetInNode, 0),
        };
      }
      if (!end && marker.end < nextOffset) {
        const offsetInNode = marker.end - currentOffset;
        end = {
          node: offsetInNode > 0 || lastTextNode === null ? node : lastTextNode,
          offset: offsetInNode > 0 ? offsetInNode : (lastTextNode?.getTextContentSize() ?? 0),
        };
        if (start && end) return false; // Stop traversal
      }
      lastTextNode = node;
    }

    currentOffset = nextOffset;
  }

  // Post-order callback
  function postVisit(node: LexicalNode) {
    if (!node.isInline()) {
      currentOffset += 2;
    }
  }

  $traverseNodes(rootNode, visit, postVisit);

  // Normal case: both start and end found
  if (start && end) {
    return { start, end, marker };
  }

  // Edge case: marker ends at the very end of the text
  if (start && marker.end <= currentOffset && lastTextNode) {
    return {
      start,
      end: {
        node: lastTextNode,
        offset: (lastTextNode as TeoTextNode).getTextContentSize(),
      },
      marker,
    };
  }

  console.warn(
    'Invalid Marker:',
    marker,
    'Last offset:',
    currentOffset,
    'Last text node:',
    lastTextNode,
  );
  return null;
}
